import React, { useState } from 'react'
import filter from 'lodash/filter'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import isInteger from 'lodash/isInteger'
import map from 'lodash/map'
import toNumber from 'lodash/toNumber'
import { InputGroup, Stack } from 'react-bootstrap'
import { useToggle } from 'react-use'
import type { MouseEvent, ReactNode } from 'react'
import type {
  PivotLayout,
  PivotLayoutAggregatedMeasure,
  PivotLayoutField,
  PivotLayoutValueFilter,
} from '@ztechapps/common.contract'
import type { SelectOption } from '@ztechapps/webui.input'
import type { PivotData } from '../pivot/types'
import {
  PivotValueFilterDirectionEnum,
  PivotValueFilterModeEnum,
  PivotValueFilterScopeEnum,
} from '@ztechapps/common.contract'
import { isPivotCalculatedMeasure } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { BasePopover } from '@ztechapps/webui.data-tools'
import { NumberInput, RadioInput, Select } from '@ztechapps/webui.input'
import { CloseButton, HorizontalStack } from '@ztechapps/webui.util'
import { findPivotLayoutItem } from '../pivot/pivotLayout.utils'
import { toPivotFieldLabel } from '../pivot/toPivotFieldLabel'
import { usePivotViewerContext } from '../PivotViewer/Context'

/** What a trigger receives — the click that opens the popover, which anchors to the clicked control. */
export type PivotTopNFilterTriggerProps = {
  onClick: (event: MouseEvent<HTMLElement>) => void
  'aria-expanded': boolean
}

/** Props for the Top / Bottom N popover of one Rows or Columns field. */
export type PivotTopNFilterProps = {
  data: PivotData
  field: PivotLayoutField
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>
  /** Called with the Top / Bottom N to set, or null to remove it. */
  onApply: (valueFilter: PivotLayoutValueFilter | null) => void
  trigger: (props: PivotTopNFilterTriggerProps) => ReactNode
}

type TopNDraft = {
  direction: PivotValueFilterDirectionEnum
  mode: PivotValueFilterModeEnum
  count: number | string
  scope: PivotValueFilterScopeEnum
  measure?: PivotLayoutValueFilter['measure']
}

const DIRECTION_OPTIONS = [
  { label: 'Top', value: PivotValueFilterDirectionEnum.top, rank: 'highest' },
  { label: 'Bottom', value: PivotValueFilterDirectionEnum.bottom, rank: 'lowest' },
]
const MODE_OPTIONS = [
  { label: 'Items', value: PivotValueFilterModeEnum.items, suffix: 'items' },
  { label: 'Percent', value: PivotValueFilterModeEnum.percent, suffix: '%' },
]
const DEFAULT_COUNT = 10

// A ranked measure is its field and its aggregation together, the way Flexmonster's value filter names it.
const toMeasureValue = (measure: PivotLayoutValueFilter['measure']): string => `${measure.field}|${measure.aggregation}`

/**
 * Popover keeping the first or last N members (or N percent of them) of one Rows or Columns field, ranked by a measure
 * and its aggregation, across the whole axis or inside each member of the field before it on the axis. A field that
 * has one has no member selection, and the other way round. OK sets it and Clear removes it through `onApply`.
 */
export const PivotTopNFilter = ({ data, field, layout, onApply, trigger }: PivotTopNFilterProps) => {
  const [show, setShow] = useToggle(false)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const { container } = usePivotViewerContext()

  const aggregatedMeasures = filter(
    layout.measures,
    (measure): measure is PivotLayoutAggregatedMeasure => !isPivotCalculatedMeasure(measure)
  )
  const measureOptions: SelectOption[] = map(aggregatedMeasures, (measure) => ({
    value: toMeasureValue(measure),
    label: `${measure.field} — ${measure.aggregation}`,
  }))
  const toDraft = (): TopNDraft => {
    const current = findPivotLayoutItem(layout.filters, field)?.valueFilter
    const first = aggregatedMeasures[0]
    return {
      direction: current?.direction ?? PivotValueFilterDirectionEnum.top,
      mode: current?.mode ?? PivotValueFilterModeEnum.items,
      count: current?.value ?? DEFAULT_COUNT,
      scope: current?.scope ?? PivotValueFilterScopeEnum.overall,
      measure: current?.measure ?? (first && { field: first.field, aggregation: first.aggregation }),
    }
  }
  const [draft, setDraft] = useState<TopNDraft>(toDraft)
  const patch = (part: Partial<TopNDraft>) => setDraft({ ...draft, ...part })

  const labelOf = (item: PivotLayoutField) =>
    toPivotFieldLabel(
      find(data.fields, (candidate) => candidate.name === item.field),
      item.field,
      item.dateGroup
    )
  const label = labelOf(field)
  // the outer field this field ranks inside; the outermost field has none, so it ranks overall only
  const axis = findPivotLayoutItem(layout.rows, field) ? layout.rows : layout.columns
  const parent = axis[findIndex(axis, (item) => item.field === field.field && item.dateGroup === field.dateGroup) - 1]
  const hasParent = parent !== undefined
  const scope = hasParent ? draft.scope : PivotValueFilterScopeEnum.overall
  const directionOption = find(DIRECTION_OPTIONS, { value: draft.direction })
  const modeOption = find(MODE_OPTIONS, { value: draft.mode })
  const count = toNumber(draft.count)
  // the entered count, else N while the field is blank or not a whole number
  const shownCount = isInteger(count) && count >= 1 ? count : 'N'
  const hint =
    draft.mode === PivotValueFilterModeEnum.percent
      ? `${directionOption?.label} ${shownCount}% keeps the ${directionOption?.rank}-ranked ${shownCount}% of members`
      : `${directionOption?.label} ${shownCount} keeps the ${shownCount} ${directionOption?.rank}-ranked members`
  const measure = draft.measure && find(measureOptions, { value: toMeasureValue(draft.measure) })
  const isValid =
    measure !== undefined &&
    isInteger(count) &&
    count >= 1 &&
    (draft.mode !== PivotValueFilterModeEnum.percent || count <= 100)
  const invalidReason =
    draft.mode === PivotValueFilterModeEnum.percent ? 'Enter 1 to 100' : 'Enter a whole number of at least 1'
  const radioName = (part: string) => `pivot-top-n-${part}-${field.field}`

  return (
    <>
      {trigger({
        // a menu item hides once clicked, so the popover anchors to the dropdown it sits in, else the clicked control
        onClick: (event) => {
          setAnchor(event.currentTarget.closest<HTMLElement>('.dropdown') ?? event.currentTarget)
          setDraft(toDraft())
          setShow(true)
        },
        'aria-expanded': show,
      })}
      <BasePopover
        name="pivot-top-n"
        show={show}
        setShow={setShow}
        target={anchor}
        placement="bottom"
        container={container}
        header={
          <HorizontalStack className="justify-content-between">
            <span>Filter: Top / Bottom N</span>
            <CloseButton onClick={() => setShow(false)} />
          </HorizontalStack>
        }
      >
        <Stack gap={2}>
          <div>
            <label className="small fw-semibold">Field</label>
            <div>{label}</div>
          </div>
          <HorizontalStack gap={3}>
            {map(DIRECTION_OPTIONS, (option) => (
              <RadioInput
                key={option.value}
                name={radioName('direction')}
                value={option.value}
                label={option.label}
                checked={draft.direction === option.value}
                onChange={() => patch({ direction: option.value })}
              />
            ))}
          </HorizontalStack>
          <HorizontalStack gap={3}>
            {map(MODE_OPTIONS, (option) => (
              <RadioInput
                key={option.value}
                name={radioName('mode')}
                value={option.value}
                label={option.label}
                checked={draft.mode === option.value}
                onChange={() => patch({ mode: option.value })}
              />
            ))}
          </HorizontalStack>
          <div>
            <label className="small fw-semibold">How many members</label>
            <InputGroup>
              <NumberInput
                value={draft.count}
                min={1}
                max={draft.mode === PivotValueFilterModeEnum.percent ? 100 : undefined}
                onChange={(event) => patch({ count: event.target.value })}
              />
              <InputGroup.Text>{modeOption?.suffix}</InputGroup.Text>
            </InputGroup>
            <div className="small text-body-secondary">{hint}</div>
          </div>
          <div>
            <label className="small fw-semibold">Ranked by</label>
            <Select
              options={measureOptions}
              value={measure}
              onChange={(option) => {
                const picked = find(
                  aggregatedMeasures,
                  (item) => toMeasureValue(item) === (option as SelectOption).value
                )
                if (picked) patch({ measure: { field: picked.field, aggregation: picked.aggregation } })
              }}
              isClearable={false}
            />
          </div>
          <div>
            <label className="small fw-semibold">Scope</label>
            <HorizontalStack gap={3}>
              <RadioInput
                name={radioName('scope')}
                value={PivotValueFilterScopeEnum.overall}
                label="Overall"
                checked={scope === PivotValueFilterScopeEnum.overall}
                onChange={() => patch({ scope: PivotValueFilterScopeEnum.overall })}
              />
              <RadioInput
                name={radioName('scope')}
                value={PivotValueFilterScopeEnum.perParent}
                label={`Within each${hasParent ? ` ${labelOf(parent)}` : ''}`}
                disabled={!hasParent}
                checked={scope === PivotValueFilterScopeEnum.perParent}
                onChange={() => patch({ scope: PivotValueFilterScopeEnum.perParent })}
              />
            </HorizontalStack>
            <div className="small text-body-secondary">
              {hasParent
                ? `Ranks the ${label} members inside each ${labelOf(parent)} member, not across the whole axis.`
                : `${label} is the outermost field, so there is no outer field to rank within.`}
            </div>
          </div>
          <div className="small text-body-secondary">
            Top / Bottom N and member selection exclude each other for a field: setting one clears the other.
          </div>
          <HorizontalStack className="justify-content-between border-top pt-3">
            <BusyButton
              text="Clear filter"
              bsStyle="warning"
              onClick={() => {
                onApply(null)
                setShow(false)
              }}
            />
            <BusyButton
              variant="primary"
              text="OK"
              disabled={!isValid}
              disabledReason={isValid ? undefined : invalidReason}
              onClick={() => {
                if (draft.measure)
                  onApply({
                    measure: draft.measure,
                    direction: draft.direction,
                    mode: draft.mode,
                    value: count,
                    scope,
                  })
                setShow(false)
              }}
            />
          </HorizontalStack>
        </Stack>
      </BasePopover>
    </>
  )
}
